import { type ReactNode } from 'react';
import { Navigate, useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { useCustomerFeatureFlags } from '../../features/flags/useCustomerFeatureFlags.js';
import { isCustomerBrowseEnabled } from '../../config/customerFeatureFlags.js';
import { resolvePlatformHubPath } from '../../features/shop/resolveShopHref.js';
import { ensureTenantInSearch } from '../../features/auth/customerAuthPaths.js';
import { JourneyShell } from '../../features/journeys/JourneyShell.js';
import { AccountSessionValidator } from '../../features/account/routing/AccountSessionValidator.js';
import { RequireCustomerAuth } from '../../features/account/routing/RequireCustomerAuth.js';
import { RequireGuestAuth } from '../../features/auth/routing/RequireGuestAuth.js';
import { hasOidcPhoneGateTokenPending } from '../../shared/session/oidcCookieLand.js';
import { PLATFORM_BROWSE_PATH } from '../../features/shop/resolveShopHref.js';
import {
  navigateToPostAuthReturnTarget,
  stripOidcLandFromSearch,
} from '../../shared/session/returnTarget.js';
import { useTenantCode } from '../../shared/tenant/TenantProvider.js';
import { isPlatformChromeTenant, PLATFORM_CHROME_TENANT_CODE } from '../platformChromeTenant.js';
import { resolveCatchAllRedirectDecision } from '../../navigation/navigationEngine.js';
import { ScreenLifecycle } from '../../shared/screen/ScreenLifecycle.js';
import { withRegisteredScreen } from './registryGuard.js';
import {
  ConfirmEmailScreen,
  ForgotPasswordScreen,
  OnboardingSetCredentialsScreen,
  RegisterScreen,
  ResetPasswordScreen,
  SignInScreen,
  SignUpPendingScreen,
  CompleteVerificationPage,
  AddPhonePage,
} from './lazyRouteComponents.js';

export function Wave1Suspense({ children }: { children: ReactNode }): JSX.Element {
  return <>{children}</>;
}

/**
 * Platform `/` home — Stage 3 default entry is Nakupovat (`/browse`).
 * Deep links `/favorites` and `/shops` remain intact (T7/T8).
 * Organizace lives at `/orgs`, not here.
 * Platform Google/Apple is NOT org-bound: `/?continueOidc=` must not strip into
 * `/shops`, and LOGIN must not hit the org picker (`/orgs`).
 * - login → `/sign-in?oidc=` (platform customer-only start)
 * - intent=sign-up → `/sign-up?oidc=` (register without org-tie on this path)
 */
export function PlatformHomeRedirect(): JSX.Element {
  const [searchParams] = useSearchParams();
  const { flags } = useCustomerFeatureFlags();

  const continueOidcRaw = searchParams.get('continueOidc');
  if (continueOidcRaw === 'google' || continueOidcRaw === 'apple') {
    const oidc = encodeURIComponent(continueOidcRaw);
    const intent = searchParams.get('intent');
    if (intent === 'sign-up') {
      return <Navigate to={`/sign-up?oidc=${oidc}`} replace />;
    }
    return <Navigate to={`/sign-in?oidc=${oidc}`} replace />;
  }
  const hubPath = isCustomerBrowseEnabled(flags)
    ? PLATFORM_BROWSE_PATH
    : resolvePlatformHubPath(flags);
  return <Navigate to={hubPath} replace />;
}

export function navigateAfterAuth(
  tenantCode: string,
  navigate: ReturnType<typeof useNavigate>
): void {
  navigateToPostAuthReturnTarget(tenantCode, navigate);
}

/**
 * Auth screens own their card h1 — JourneyShell supplies shared tab content
 * layout only (CUSTOMER_TAB_CONTENT_CLASS), not a duplicate page title.
 */
export function SignInRoute(): JSX.Element {
  const navigate = useNavigate();
  // G20 — never invent literal `default` when :tenantCode is momentarily missing;
  // TenantProvider already resolves the real URL slug (refresh must not dual-hit default + home).
  const tenantCode = useTenantCode();

  return withRegisteredScreen(
    'MS-AUTH-010',
    <JourneyShell>
      <RequireGuestAuth>
        <ScreenLifecycle screenId="MS-AUTH-010">
          <SignInScreen
            onSignedIn={() => {
              navigateAfterAuth(tenantCode, navigate);
            }}
          />
        </ScreenLifecycle>
      </RequireGuestAuth>
    </JourneyShell>,
  );
}

/**
 * G4 — platform `/sign-in` post-auth consume tenant.
 * Login must not key off `?tenant=` (product freeze). Always platform chrome;
 * per-tenant parks are consumed from path-tenant `SignInRoute` only.
 * `search` retained for test/call-site compatibility; ignored.
 */
export function resolvePlatformSignInPostAuthTenant(_search?: string): string {
  void _search;
  return PLATFORM_CHROME_TENANT_CODE;
}

/**
 * Platform `/sign-in` — tenant-less login (home membership resolved by API).
 * Never binds login org via `?tenant=`; platform allowlist park still wins.
 * Path-tenant return parks: use `/{tenant}/sign-in` (`SignInRoute`).
 */
export function PlatformSignInRoute(): JSX.Element {
  const navigate = useNavigate();
  const postAuthTenant = resolvePlatformSignInPostAuthTenant();

  return withRegisteredScreen(
    'MS-AUTH-010',
    <JourneyShell>
      <RequireGuestAuth>
        <ScreenLifecycle screenId="MS-AUTH-010">
          <SignInScreen
            onSignedIn={() => {
              navigateToPostAuthReturnTarget(postAuthTenant, navigate);
            }}
          />
        </ScreenLifecycle>
      </RequireGuestAuth>
    </JourneyShell>,
  );
}

/** Platform `/sign-up` — registration form with org select (not the Organizace directory). */
export function PlatformSignUpRoute(): JSX.Element {
  return withRegisteredScreen(
    'MS-AUTH-011',
    <JourneyShell>
      <RequireGuestAuth>
        <ScreenLifecycle screenId="MS-AUTH-011">
          <RegisterScreen />
        </ScreenLifecycle>
      </RequireGuestAuth>
    </JourneyShell>,
  );
}

export function RegisterRoute(): JSX.Element {
  return withRegisteredScreen(
    'MS-AUTH-011',
    <JourneyShell>
      <RequireGuestAuth>
        <RegisterScreen />
      </RequireGuestAuth>
    </JourneyShell>,
  );
}

export function SignUpPendingRoute(): JSX.Element {
  return withRegisteredScreen(
    'MS-AUTH-012',
    <JourneyShell>
      <RequireGuestAuth>
        <SignUpPendingScreen />
      </RequireGuestAuth>
    </JourneyShell>,
  );
}

export function TenantCatchAllRedirect(): JSX.Element {
  const tenantCode = useTenantCode();
  const { pathname } = useLocation();
  const decision = resolveCatchAllRedirectDecision(pathname, tenantCode);

  // B5 — soft Navigate for canonical sign-in catch-all (no window.location.replace).
  return <Navigate to={decision.target} replace />;
}

export function LegacyConfirmEmailRedirect(): JSX.Element {
  const location = useLocation();
  const pathSlug = useTenantCode();
  return (
    <Navigate
      to={{
        // Wave C / G13 — platform confirm-email; inject tenant=<pathSlug> when missing.
        pathname: '/confirm-email',
        // Strip oidcLand: RR search can lag after replaceState force-strip.
        search: ensureTenantInSearch(
          stripOidcLandFromSearch(location.search),
          pathSlug,
        ),
        hash: location.hash,
      }}
      replace
    />
  );
}

/**
 * Not guest-only: confirm-email must run with an email token even if another
 * session exists (or while establishing a new one). Dual chrome is avoided by
 * bottom-nav auth-path rules; do not bounce signed-in users off this screen.
 */
export function ConfirmEmailRoute(): JSX.Element {
  return withRegisteredScreen(
    'MS-AUTH-013',
    <JourneyShell>
      <ConfirmEmailScreen />
    </JourneyShell>,
  );
}

export function OnboardingSetCredentialsRoute(): JSX.Element {
  const tenantCode = useTenantCode();
  const platformChrome = isPlatformChromeTenant(tenantCode);
  const screen = <OnboardingSetCredentialsScreen />;
  return withRegisteredScreen(
    'MS-AUTH-014',
    <JourneyShell>
      {platformChrome ? (
        screen
      ) : (
        <RequireCustomerAuth>{screen}</RequireCustomerAuth>
      )}
    </JourneyShell>,
  );
}

export function ForgotPasswordRoute(): JSX.Element {
  return withRegisteredScreen(
    'MS-AUTH-015',
    <JourneyShell>
      <RequireGuestAuth>
        <ForgotPasswordScreen />
      </RequireGuestAuth>
    </JourneyShell>,
  );
}

/**
 * Guest-only: reset uses email-token query; product does not support in-session
 * reset without conflict (`useResetPassword` ends all sessions on success).
 * If globally signed in, redirect away — user must logout first to use the link.
 */
export function ResetPasswordRoute(): JSX.Element {
  return withRegisteredScreen(
    'MS-AUTH-016',
    <JourneyShell>
      <RequireGuestAuth>
        <ResetPasswordScreen />
      </RequireGuestAuth>
    </JourneyShell>,
  );
}

/** MS-AUTH-019 — Dokončit ověření (account shell child). */
export function CompleteVerificationRouteElement(): JSX.Element {
  return withRegisteredScreen(
    'MS-AUTH-019',
    <Wave1Suspense>
      <CompleteVerificationPage />
    </Wave1Suspense>,
  );
}

/**
 * MS-AUTH-020 — add-phone gate (Wave 4.6 G9).
 * Mounted as App.tsx sibling of ConfirmEmail — OUTSIDE TenantAccountRoutes.
 * Token mode: no RequireCustomerAuth / AccountSessionValidator / useAccountMe.
 * Cookie leftover / oidcOfferPhone: require session (guest → /sign-in).
 *
 * G4 — `oidcOfferPhone=1` is landing-pending ({@link hasOidcLandingPending}).
 * CustomerAppShell park restore waits until Skip/complete navigates away;
 * product offer stays; no `?tenant=` login bind for restore.
 */
export function AddPhoneGateRoute(): JSX.Element {
  const [searchParams] = useSearchParams();
  const tokenMode = hasOidcPhoneGateTokenPending(
    `?${searchParams.toString()}`,
  );
  const page = (
    <Wave1Suspense>
      <JourneyShell>
        <AddPhonePage />
      </JourneyShell>
    </Wave1Suspense>
  );
  if (tokenMode) {
    return withRegisteredScreen('MS-AUTH-020', page);
  }
  return withRegisteredScreen(
    'MS-AUTH-020',
    <RequireCustomerAuth>
      <AccountSessionValidator>{page}</AccountSessionValidator>
    </RequireCustomerAuth>,
  );
}

/** @deprecated Prefer {@link AddPhoneGateRoute} (App.tsx sibling). Kept for registry name stability. */
export function AddPhoneRouteElement(): JSX.Element {
  return <AddPhoneGateRoute />;
}
